import { Routes, Route, Navigate } from 'react-router-dom'
import { CssBaseline, Grid2 as Grid, useMediaQuery } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { RootState } from './redux/reducers'
import { Provider } from 'react-redux'
import store from './redux/Store'
import SideNav from './features/SideNav'
import Topbar from './features/Topbar'
import Notification from './components/Notification'
import { useAppDispatch, useAppSelector } from './redux/Hook'
import { initialAlertState } from './redux/reducers/NotificationState'
import { routes as appRoutes } from './Routes'

// Extend the Theme interface to include custom colors
declare module '@mui/material/styles' {
  interface Theme {
    customColors: {
      button: string
      btnGreen?: string
      textColorMain?: string
      textColorSecondary?: string
    }
  }

  // Allow configuration using `ThemeOptions`
  interface ThemeOptions {
    customColors?: {
      button?: string
      btnGreen?: string
      btnBlack?: string
      textColorMain?: string
      textColorSecondary?: string
    }
  }
}

function App() {
  const dispatch = useAppDispatch()
  const themeDataState = useAppSelector((state: RootState) => state.updateTheme)
  const notifyDataState = useAppSelector(
    (state: RootState) => state.notificationAlert
  )

  const isTabletOrBelow = useMediaQuery('(max-width: 768px)')

  const theme = createTheme({
    palette: {
      mode: themeDataState?.mode,
      primary: {
        main: themeDataState?.primary,
        light: themeDataState?.primaryLight,
        contrastText: '#f8f9fa',
      },
      secondary: {
        main: themeDataState?.secondary,
        contrastText: '#f8f9fa',
      },
      error: {
        main: themeDataState?.error,
      },
    },
    typography: {
      fontFamily: 'Helvetica, Arial, sans-serif',
    },
    customColors: {
      button: '#FF8C00',
      btnGreen: '#328301',
      btnBlack: '#2F2F2F',
      textColorMain: '#333333',
      textColorSecondary: '#646464',
    },
  })

  // Function to reset notification data
  const resetNotificationData = () => {
    dispatch({
      type: 'SEND_ALERT',
      data: {
        enable: initialAlertState.enable,
        type: initialAlertState.type,
        message: initialAlertState.message,
        duration: initialAlertState.duration,
      },
    })
  }

  // const PlaceholderComponent: React.FC = () => (
  //   <div>Page Under Construction</div>
  // )

  return (
    <ThemeProvider theme={theme}>
      <Notification
        open={notifyDataState.enable}
        type={notifyDataState.type}
        message={notifyDataState.message}
        duration={notifyDataState.duration}
        setOpen={() => resetNotificationData()}
      />
      <CssBaseline />
      <div className='app'>
        {!isTabletOrBelow && <SideNav />}
        <Grid
          container
          direction={'column'}
          width={'100%'}
          height={'100%'}
          spacing={1.5}
        >
          <Grid size={{ xs: 12 }}>
            <Topbar />
          </Grid>
          <Grid container direction={'column'} className='content'>
            <Routes>
              {appRoutes?.map((route, index) => (
                <Route
                  key={index}
                  path={route?.path}
                  element={<route.component />}
                />
              ))}
              <Route path='*' element={<Navigate to='/dashboard' replace />} />
            </Routes>
          </Grid>
        </Grid>
      </div>
    </ThemeProvider>
  )
}

function Root() {
  return (
    <Provider store={store}>
      <App />
    </Provider>
  )
}

export default Root
